<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
 formId: {
 type: [String, Number],
 default: null,
 },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { accountScopedRoute } = useAccount();

const uiFlags = useMapGetter('forms/getUIFlags');

const resolvedFormId = computed(
 () => props.formId || route.params.formId
);

const form = computed(() => {
 return store.getters['forms/getById'](resolvedFormId.value);
});

const submissionsData = computed(() => {
 return store.getters['forms/getSubmissions'](
 Number(resolvedFormId.value)
 );
});

const submissions = computed(() => submissionsData.value?.data || []);
const meta = computed(() => submissionsData.value?.meta || {});

const currentPage = ref(1);
const expandedRow = ref(null);
const activeStatus = ref('all');
const perPage = 25;

const statusTabs = [
 { key: 'all', label: 'Todos' },
 { key: 'completed', label: 'Completos' },
 { key: 'partial', label: 'Parciais' },
];

const totalPages = computed(() => {
 const total = meta.value.total_count || meta.value.total || submissions.value.length;
 return Math.ceil(total / perPage);
});

function statusBadge(status) {
 const map = {
 completed: {
 label: 'Completo',
 class:
 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400',
 },
 partial: {
 label: 'Parcial',
 class:
 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
 },
 superseded: {
 label: 'Substituido',
 class:
 'bg-n-slate-3 text-n-slate-10 dark:text-n-slate-9',
 },
 };
 return map[status] || map.completed;
}

function formatDate(dateStr) {
 if (!dateStr) return '--';
 return new Date(dateStr).toLocaleDateString('pt-BR', {
 day: '2-digit',
 month: 'short',
 year: 'numeric',
 hour: '2-digit',
 minute: '2-digit',
 });
}

function toggleRow(id) {
 expandedRow.value = expandedRow.value === id ? null : id;
}

function previewData(data) {
 if (!data || typeof data !== 'object') return '--';
 const entries = Object.entries(data).filter(
 ([, v]) => v && v.toString().trim() !== ''
 );
 if (entries.length === 0) return '--';
 return entries
 .slice(0, 3)
 .map(([k, v]) => `${k}: ${v}`)
 .join(', ');
}

function fetchSubmissions() {
 if (!resolvedFormId.value) return;
 const params = { page: currentPage.value, per_page: perPage };
 if (activeStatus.value !== 'all') {
 params.status = activeStatus.value;
 }
 store.dispatch('forms/fetchSubmissions', {
 formId: Number(resolvedFormId.value),
 params,
 });
}

function onStatusChange(status) {
 activeStatus.value = status;
 currentPage.value = 1;
 fetchSubmissions();
}

function onPrevPage() {
 if (currentPage.value > 1) {
 currentPage.value -= 1;
 }
}

function onNextPage() {
 if (currentPage.value < totalPages.value) {
 currentPage.value += 1;
 }
}

function onBack() {
 router.push(accountScopedRoute('forms_index'));
}

watch(currentPage, fetchSubmissions);

onMounted(() => {
 if (resolvedFormId.value) {
 store.dispatch('forms/show', Number(resolvedFormId.value));
 fetchSubmissions();
 }
});
</script>

<template>
 <div class="flex flex-col h-full w-full bg-n-solid-1">
 <!-- Header -->
 <div
 class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
 >
 <div class="flex items-center gap-3">
 <button
 class="flex items-center justify-center size-8 rounded-lg text-n-slate-10 hover:text-n-slate-11 hover:bg-n-alpha-1 dark:text-n-slate-9 transition-colors"
 @click="onBack"
 >
 <span class="i-lucide-arrow-left size-4" />
 </button>
 <div>
 <h1
 class="text-lg font-semibold text-n-slate-12"
 >
 {{ form?.name || 'Submissoes' }}
 </h1>
 <p class="text-xs text-n-slate-10 !mb-0">
 {{ meta.total_count || meta.total || submissions.length }}
 submissoes
 </p>
 </div>
 </div>
 </div>

 <!-- Status filter tabs -->
 <div
 class="flex items-center gap-1 px-6 py-2 border-b border-n-weak"
 >
 <button
 v-for="tab in statusTabs"
 :key="tab.key"
 class="px-3 py-1.5 text-xs font-medium rounded-full transition-colors"
 :class="
 activeStatus === tab.key
 ? 'bg-woot-50 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400'
 : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 dark:hover:bg-n-slate-12'
 "
 @click="onStatusChange(tab.key)"
 >
 {{ tab.label }}
 </button>
 </div>

 <!-- Content -->
 <div class="flex-1 overflow-y-auto">
 <!-- Loading -->
 <div
 v-if="uiFlags.isFetchingSubmissions && !submissions.length"
 class="flex items-center justify-center py-16"
 >
 <div
 class="size-8 border-2 border-woot-500 border-t-transparent rounded-full animate-spin"
 />
 </div>

 <!-- Empty state -->
 <div
 v-else-if="!submissions.length"
 class="flex flex-col items-center justify-center h-full text-center px-6"
 >
 <div
 class="flex items-center justify-center size-16 rounded-2xl bg-n-slate-3 mb-4"
 >
 <span class="i-lucide-inbox size-8 text-n-slate-9" />
 </div>
 <h3
 class="text-base font-semibold text-n-slate-12 mb-1"
 >
 Nenhuma submissão
 </h3>
 <p
 class="text-sm text-n-slate-10 !mb-0 max-w-sm"
 >
 {{
 activeStatus === 'partial'
 ? 'Nenhum preenchimento parcial registrado.'
 : 'Este formulário ainda não recebeu nenhuma submissão.'
 }}
 </p>
 </div>

 <!-- Table -->
 <table v-else class="w-full">
 <thead>
 <tr
 class="border-b border-n-weak"
 >
 <th
 class="text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider px-6 py-3"
 >
 Contato
 </th>
 <th
 class="text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider px-4 py-3"
 >
 Email
 </th>
 <th
 class="text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider px-4 py-3"
 >
 Status
 </th>
 <th
 class="text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider px-4 py-3"
 >
 Data
 </th>
 <th
 class="text-left text-xs font-medium text-n-slate-10 uppercase tracking-wider px-4 py-3"
 >
 Dados
 </th>
 <th
 class="text-right text-xs font-medium text-n-slate-10 uppercase tracking-wider px-6 py-3"
 >
 Ações
 </th>
 </tr>
 </thead>
 <tbody>
 <template
 v-for="submission in submissions"
 :key="submission.id"
 >
 <tr
 class="group border-b border-n-weak hover:bg-n-alpha-1/50 transition-colors cursor-pointer"
 @click="toggleRow(submission.id)"
 >
 <td class="px-6 py-3.5">
 <span
 class="text-sm font-medium text-n-slate-12"
 >
 {{
 submission.contact_name ||
 submission.data?.name ||
 submission.data?.nome ||
 '--'
 }}
 </span>
 </td>
 <td class="px-4 py-3.5">
 <span
 class="text-sm text-n-slate-11"
 >
 {{
 submission.contact_email ||
 submission.data?.email ||
 '--'
 }}
 </span>
 </td>
 <td class="px-4 py-3.5">
 <span
 class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
 :class="statusBadge(submission.status).class"
 >
 {{ statusBadge(submission.status).label }}
 </span>
 </td>
 <td class="px-4 py-3.5">
 <span
 class="text-sm text-n-slate-11"
 >
 {{ formatDate(submission.created_at) }}
 </span>
 </td>
 <td class="px-4 py-3.5">
 <span
 class="text-sm text-n-slate-10 truncate block max-w-xs"
 >
 {{ previewData(submission.data) }}
 </span>
 </td>
 <td class="px-6 py-3.5 text-right">
 <button
 class="text-n-slate-9 hover:text-n-slate-11 dark:hover:text-n-slate-7 transition-colors"
 >
 <span
 class="i-lucide-chevron-down size-4 transition-transform"
 :class="
 expandedRow === submission.id ? 'rotate-180' : ''
 "
 />
 </button>
 </td>
 </tr>

 <!-- Expanded row -->
 <tr v-if="expandedRow === submission.id">
 <td
 colspan="6"
 class="px-6 py-4 bg-n-slate-2/50 dark:bg-n-slate-12/30"
 >
 <div class="grid grid-cols-2 gap-3 max-w-2xl">
 <div
 v-for="(value, key) in submission.data || {}"
 :key="key"
 class="bg-n-solid-1 rounded-lg p-3 border border-n-weak"
 >
 <p
 class="text-[10px] font-medium text-n-slate-9 uppercase tracking-wider !mb-0.5"
 >
 {{ key }}
 </p>
 <p
 class="text-sm text-n-slate-12 !mb-0 break-words"
 >
 {{ value || '--' }}
 </p>
 </div>
 </div>
 </td>
 </tr>
 </template>
 </tbody>
 </table>
 </div>

 <!-- Pagination -->
 <div
 v-if="totalPages > 1"
 class="flex items-center justify-between px-6 py-3 border-t border-n-weak shrink-0"
 >
 <p class="text-sm text-n-slate-10 !mb-0">
 Pagina {{ currentPage }} de {{ totalPages }}
 </p>
 <div class="flex items-center gap-2">
 <button
 :disabled="currentPage <= 1"
 class="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
 @click="onPrevPage"
 >
 <span class="i-lucide-chevron-left size-4" />
 Anterior
 </button>
 <button
 :disabled="currentPage >= totalPages"
 class="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
 @click="onNextPage"
 >
 Proximo
 <span class="i-lucide-chevron-right size-4" />
 </button>
 </div>
 </div>
 </div>
</template>
